import { approvalListResponseSchema, approvalResponseSchema } from '@coworker/contracts';
import type { ApprovalListResponse } from '@coworker/contracts';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { useAuth } from '../auth/AuthProvider.js';
import { ApiError } from '../lib/api-error.js';
import { useCurrentWorkspace } from './use-current-workspace.js';
import { useRecoverSession } from './use-recover-session.js';

/**
 * What the employee is waiting to be allowed to do - AGT-008.
 *
 * Keyed by workspace, like every other list here: an approval belongs to a
 * workspace and a person may be in several.
 */
export const approvalsQueryKey = (workspaceId: string | undefined) =>
  ['approvals', workspaceId] as const;

export interface ApprovalsState {
  readonly query: UseQueryResult<ApprovalListResponse, unknown>;
  readonly isUnauthenticated: boolean;
}

/**
 * Polled, because the list changes without this screen doing anything.
 *
 * A run reaches an approval while somebody is looking at another tab; the
 * request appears then, not when they next navigate.
 *
 * **Slower than the chat's two seconds, deliberately.** `RUN_POLL_MS` is
 * following one run whose answer somebody is waiting for; this is a standing
 * list on a screen that is usually open in the background, and five seconds is
 * a request every twelve minutes per idle tab rather than one every thirty.
 * Nobody is watching this list for a sub-second arrival - they came to it
 * because something was already waiting.
 */
export const APPROVALS_POLL_MS = 5_000;

export function useApprovals(): ApprovalsState {
  const { api, status } = useAuth();
  const workspace = useCurrentWorkspace();

  const query = useQuery({
    queryKey: approvalsQueryKey(workspace?.id),
    enabled: status === 'signedIn' && workspace !== undefined,
    queryFn: () =>
      api.request('/v1/approvals', {
        schema: approvalListResponseSchema,
        workspaceId: workspace?.id ?? '',
      }),
    refetchInterval: APPROVALS_POLL_MS,
  });

  const isUnauthenticated = query.error instanceof ApiError && query.error.isUnauthenticated;
  useRecoverSession({ isUnauthenticated, erroredAt: query.errorUpdatedAt });

  return { query, isUnauthenticated };
}

/**
 * Answers one.
 *
 * **The list is invalidated rather than written into.** Unlike the tool picker,
 * the response here is one row and the list is many; and a decision resumes a
 * run, which may reach a second approval within the poll interval. Refetching
 * is the only thing that can show that.
 */
export function useDecideApproval() {
  const { api } = useAuth();
  const workspace = useCurrentWorkspace();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { readonly id: string; readonly decision: 'APPROVED' | 'REJECTED' }) =>
      api.request(`/v1/approvals/${input.id}/decision`, {
        method: 'POST',
        schema: approvalResponseSchema,
        workspaceId: workspace?.id ?? '',
        body: { decision: input.decision },
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: approvalsQueryKey(workspace?.id) });
    },
  });
}
